export const calculateNetAmount = (
  total: number | string,
  overhead: number
): string => {
  const overheadFee = calculateOverheadFee(total, overhead);
  return (Number(total) - Number(overheadFee)).toFixed(2);
};

export const calculateOverheadFee = (
  total: number | string,
  overhead: number
): string => {
  const overheadDecimal = overhead / 100;
  return (Number(total) * overheadDecimal).toFixed(2);
};

const LOCATION_OF_SERVICE_CODES: any = {
  W: {
    W: { CODE: 'W', MODIFIER: 1.0 },
    B: { CODE: 'M', MODIFIER: 1.025 },
    R: { CODE: 'M', MODIFIER: 1.025 },
    N: { CODE: 'P', MODIFIER: 1.125 },
    RE: { CODE: 'Q', MODIFIER: 1.175 },
    C: { CODE: 'Q', MODIFIER: 1.175 },
    MB: { CODE: 'W', MODIFIER: 1.0 },
    'NW-ON': { CODE: 'W', MODIFIER: 1.0 },
    NU: { CODE: 'W', MODIFIER: 1.0 },
    O: { CODE: 'W', MODIFIER: 1.0 },
  },
  B: {
    W: { CODE: 'S', MODIFIER: 1.025 },
    B: { CODE: 'T', MODIFIER: 1.05 },
    R: { CODE: 'T', MODIFIER: 1.05 },
    N: { CODE: 'U', MODIFIER: 1.15 },
    RE: { CODE: 'V', MODIFIER: 1.2 },
    C: { CODE: 'V', MODIFIER: 1.2 },
    MB: { CODE: 'W', MODIFIER: 1.0 },
    'NW-ON': { CODE: 'W', MODIFIER: 1.0 },
    NU: { CODE: 'W', MODIFIER: 1.0 },
    O: { CODE: 'W', MODIFIER: 1.0 },
  },
  R: {
    W: { CODE: 'S', MODIFIER: 1.025 },
    B: { CODE: 'T', MODIFIER: 1.05 },
    R: { CODE: 'T', MODIFIER: 1.05 },
    N: { CODE: 'U', MODIFIER: 1.15 },
    RE: { CODE: 'V', MODIFIER: 1.2 },
    C: { CODE: 'V', MODIFIER: 1.2 },
    MB: { CODE: 'W', MODIFIER: 1.0 },
    'NW-ON': { CODE: 'W', MODIFIER: 1.0 },
    NU: { CODE: 'W', MODIFIER: 1.0 },
    O: { CODE: 'W', MODIFIER: 1.0 },
  },
  N: {
    W: { CODE: 'H', MODIFIER: 1.125 },
    B: { CODE: 'X', MODIFIER: 1.15 },
    R: { CODE: 'X', MODIFIER: 1.15 },
    N: { CODE: 'Y', MODIFIER: 1.25 },
    RE: { CODE: 'Z', MODIFIER: 1.3 },
    C: { CODE: 'Z', MODIFIER: 1.3 },
    MB: { CODE: 'W', MODIFIER: 1.0 },
    'NW-ON': { CODE: 'W', MODIFIER: 1.0 },
    NU: { CODE: 'W', MODIFIER: 1.0 },
    O: { CODE: 'W', MODIFIER: 1.0 },
  },
  C: {
    W: { CODE: 'D', MODIFIER: 1.175 },
    B: { CODE: 'F', MODIFIER: 1.2 },
    R: { CODE: 'F', MODIFIER: 1.2 },
    N: { CODE: 'G', MODIFIER: 1.3 },
    RE: { CODE: 'A', MODIFIER: 1.35 },
    C: { CODE: 'A', MODIFIER: 1.35 },
    MB: { CODE: 'W', MODIFIER: 1.0 },
    'NW-ON': { CODE: 'W', MODIFIER: 1.0 },
    NU: { CODE: 'W', MODIFIER: 1.0 },
    O: { CODE: 'W', MODIFIER: 1.0 },
  },
};

const DEFAULT_LOCATION_SERVICE_CODES: Record<
  string,
  { code: string; modifier: number; claimLocation: 'physical' | 'virtual' }
> = {
  W: { code: 'W', modifier: 1.0, claimLocation: 'physical' },
  B: { code: 'B', modifier: 1.05, claimLocation: 'physical' },
  R: { code: 'R', modifier: 1.05, claimLocation: 'physical' },
  N: { code: 'N', modifier: 1.25, claimLocation: 'physical' },
  C: { code: 'C', modifier: 1.35, claimLocation: 'physical' },
  O: { code: 'O', modifier: 1.0, claimLocation: 'physical' },
};

const getDefaultLocationServiceRate = (
  locationOfService: string | null | undefined
) => {
  if (locationOfService) {
    const configuredRate = DEFAULT_LOCATION_SERVICE_CODES[locationOfService];
    if (configuredRate) {
      return configuredRate;
    }
  }

  return {
    modifier: 1.0,
    code: locationOfService || 'W',
    claimLocation: 'physical' as const,
  };
};

export const calculateLocationServiceRate = (
  virtualPhysicianRegion: string | null | undefined,
  locationOfService: string | null | undefined,
  payorProvince?: string | null
) => {
  if (payorProvince === 'BC') {
    return {
      code: locationOfService || '',
      modifier: 1,
      claimLocation: 'physical' as const,
    };
  }

  const defaultRate = getDefaultLocationServiceRate(locationOfService);

  if (!virtualPhysicianRegion || !locationOfService) {
    return defaultRate;
  }

  const locationMap: { [key: string]: string } = {
    Winnipeg: 'W',
    W: 'W',
    Brandon: 'B',
    B: 'B',
    Rural: 'R',
    R: 'R',
    Northern: 'N',
    N: 'N',
    Remote: 'C',
    RE: 'C',
    C: 'C',
  };

  const physicianLoc = locationMap[virtualPhysicianRegion] as string;

  if (!physicianLoc) {
    return defaultRate;
  }

  const match = LOCATION_OF_SERVICE_CODES[physicianLoc][locationOfService];
  if (!match) {
    return defaultRate;
  }

  const { CODE, MODIFIER } = match;

  if (!CODE || !MODIFIER) {
    return defaultRate;
  }
  return {
    code: CODE,
    modifier: MODIFIER,
    claimLocation: 'virtual' as const,
  };
};
